import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from '../auth/AuthContext';
import { ToastProvider } from '../components/ui';
import { AuditPage } from '../pages/Audit';
import { LocationsPage } from '../pages/Locations';
import { LoginHistoryPage } from '../pages/LoginHistory';
import { RolesPage } from '../pages/Roles';
import { SecurityPage } from '../pages/Security';
import { SettingsPage } from '../pages/Settings';
import { UserDetailPage } from '../pages/UserDetail';
import { UsersPage } from '../pages/Users';
import { AdminsPage } from './Admins';
import { MasterDashboard } from './MasterDashboard';
import { MasterLogin } from './MasterLogin';
import { MasterShell } from './MasterShell';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, error) => {
        const status = (error as { status?: number }).status ?? 0;
        return status >= 500 && count < 2;
      },
    },
  },
});

function Gate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="skeleton" style={{ height: '100vh' }} />;
  // The auth context only keeps a super admin in master mode, so reaching
  // here signed in means being one. The server checks again on every call.
  if (!user) return <MasterLogin />;

  return (
    <Routes>
      <Route element={<MasterShell />}>
        <Route index element={<MasterDashboard />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="admins" element={<AdminsPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="login-history" element={<LoginHistoryPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="locations" element={<LocationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function MasterApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Gate />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
